import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../api';

const STATUS_LABELS = {
  desired: 'Desired',
  possible: 'Possible',
  save_the_date: 'Save the Date',
  rsvped: "RSVP'd",
};

const STATUS_COLORS = {
  desired: '#818cf8',
  possible: '#fbbf24',
  save_the_date: '#f97316',
  rsvped: '#34d399',
};

const SIDE_COLORS = {
  Tim: '#60a5fa',
  Emilio: '#c084fc',
};

const RELATIONSHIP_LABELS = {
  family: 'Family',
  friend: 'Friend',
  coworker: 'Coworker',
};

const RELATIONSHIP_COLORS = {
  family: '#f472b6',
  friend: '#38bdf8',
  coworker: '#a3e635',
};

const EMPTY_FORM = {
  name: '',
  side: 'Tim',
  status: 'desired',
  additionalGuests: [],
  notes: '',
  address: '',
  inviteSent: false,
  dietaryRestrictions: '',
  tableAssignment: '',
  email: '',
  phone: '',
  rsvpResponse: '',
  relationship: '',
};

function GuestModal({ guest, onSave, onClose }) {
  const [form, setForm] = useState(
    guest
      ? {
          name: guest.name,
          side: guest.side,
          status: guest.status,
          additionalGuests: guest.additionalGuests || [],
          notes: guest.notes,
          address: guest.address || '',
          inviteSent: guest.inviteSent || false,
          dietaryRestrictions: guest.dietaryRestrictions || '',
          tableAssignment: guest.tableAssignment || '',
          email: guest.email || '',
          phone: guest.phone || '',
          rsvpResponse: guest.rsvpResponse || '',
          relationship: guest.relationship || '',
        }
      : { ...EMPTY_FORM, additionalGuests: [] }
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function handleChange(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function addGuest() {
    setForm((prev) => ({ ...prev, additionalGuests: [...prev.additionalGuests, ''] }));
  }

  function updateGuestName(index, name) {
    setForm((prev) => {
      const updated = [...prev.additionalGuests];
      updated[index] = name;
      return { ...prev, additionalGuests: updated };
    });
  }

  function removeGuest(index) {
    setForm((prev) => ({
      ...prev,
      additionalGuests: prev.additionalGuests.filter((_, i) => i !== index),
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave({
        ...form,
        name: form.name.trim(),
        additionalGuests: form.additionalGuests.map((g) => g.trim()),
        notes: form.notes.trim(),
        address: form.address.trim(),
        dietaryRestrictions: form.dietaryRestrictions.trim(),
        tableAssignment: form.tableAssignment.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
      });
    } catch {
      setError('Failed to save guest');
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content wg-modal" onClick={(e) => e.stopPropagation()}>
        <h2>{guest ? 'Edit Guest' : 'Add Guest'}</h2>
        <form onSubmit={handleSubmit} className="wg-form">
          <div className="wg-form-row">
            <label className="wg-form-label">Name *</label>
            <input className="wg-form-input" type="text" value={form.name}
              onChange={(e) => handleChange('name', e.target.value)} placeholder="Guest name" autoFocus />
          </div>

          <div className="wg-form-row">
            <label className="wg-form-label">Side</label>
            <div className="wg-side-toggle">
              {['Tim', 'Emilio'].map((s) => (
                <button key={s} type="button"
                  className={`wg-side-btn${form.side === s ? ' active' : ''}`}
                  style={form.side === s ? { background: SIDE_COLORS[s], color: '#0f1117' } : {}}
                  onClick={() => handleChange('side', s)}>{s}</button>
              ))}
            </div>
          </div>

          <div className="wg-form-row">
            <label className="wg-form-label">Relationship</label>
            <select className="wg-form-select" value={form.relationship}
              onChange={(e) => handleChange('relationship', e.target.value)}>
              <option value="">Not specified</option>
              {Object.entries(RELATIONSHIP_LABELS).map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
          </div>

          <div className="wg-form-row">
            <label className="wg-form-label">Status</label>
            <select className="wg-form-select" value={form.status}
              onChange={(e) => handleChange('status', e.target.value)}>
              {Object.entries(STATUS_LABELS).map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
          </div>

          <div className="wg-form-row">
            <label className="wg-form-label">Additional Guests ({form.additionalGuests.length})</label>
            <button type="button" className="wg-btn wg-btn-small" onClick={addGuest}>+ Add Guest</button>
          </div>
          {form.additionalGuests.map((guestName, i) => (
            <div key={i} className="wg-form-row wg-additional-guest-row">
              <input className="wg-form-input" type="text" value={guestName}
                onChange={(e) => updateGuestName(i, e.target.value)}
                placeholder={`Guest ${i + 1} name (optional)`} />
              <button type="button" className="wg-btn wg-btn-remove" onClick={() => removeGuest(i)}>&times;</button>
            </div>
          ))}

          <div className="wg-form-row">
            <label className="wg-form-label">Address</label>
            <textarea className="wg-form-textarea" value={form.address}
              onChange={(e) => handleChange('address', e.target.value)}
              placeholder="Mailing address for invite" rows={2} />
          </div>

          <div className="wg-form-row">
            <label className="wg-form-label">Email</label>
            <input className="wg-form-input" type="email" value={form.email}
              onChange={(e) => handleChange('email', e.target.value)} placeholder="Email address" />
          </div>

          <div className="wg-form-row">
            <label className="wg-form-label">Phone</label>
            <input className="wg-form-input" type="tel" value={form.phone}
              onChange={(e) => handleChange('phone', e.target.value)} placeholder="Phone number" />
          </div>

          <div className="wg-form-row wg-form-check-row">
            <label className="wg-form-label">Invite Sent</label>
            <input type="checkbox" checked={form.inviteSent}
              onChange={(e) => handleChange('inviteSent', e.target.checked)} className="wg-form-checkbox" />
          </div>

          <div className="wg-form-row">
            <label className="wg-form-label">RSVP Response</label>
            <select className="wg-form-select" value={form.rsvpResponse}
              onChange={(e) => handleChange('rsvpResponse', e.target.value)}>
              <option value="">Pending</option>
              <option value="attending">Attending</option>
              <option value="declined">Declined</option>
            </select>
          </div>

          <div className="wg-form-row">
            <label className="wg-form-label">Dietary Restrictions</label>
            <input className="wg-form-input" type="text" value={form.dietaryRestrictions}
              onChange={(e) => handleChange('dietaryRestrictions', e.target.value)}
              placeholder="e.g., Vegetarian, Gluten-free" />
          </div>

          <div className="wg-form-row">
            <label className="wg-form-label">Table Assignment</label>
            <input className="wg-form-input" type="text" value={form.tableAssignment}
              onChange={(e) => handleChange('tableAssignment', e.target.value)} placeholder="e.g., Table 5" />
          </div>

          <div className="wg-form-row">
            <label className="wg-form-label">Notes</label>
            <textarea className="wg-form-textarea" value={form.notes}
              onChange={(e) => handleChange('notes', e.target.value)} placeholder="Any notes..." rows={2} />
          </div>

          {error && <div className="wg-form-error">{error}</div>}

          <div className="wg-form-actions">
            <button type="button" className="wg-btn wg-btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="wg-btn wg-btn-save" disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function WeddingGuestTracker({ refreshKey }) {
  const [guests, setGuests] = useState([]);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [sideFilter, setSideFilter] = useState('all');
  const [inviteFilter, setInviteFilter] = useState('all'); // 'all' | 'sent' | 'not_sent'
  const [hideDeclined, setHideDeclined] = useState(true);
  const [hideAttending, setHideAttending] = useState(false);
  const [modalGuest, setModalGuest] = useState(undefined);
  const [showModal, setShowModal] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [guestsRes, countsRes] = await Promise.all([
        apiFetch('/api/wedding-guests'),
        apiFetch('/api/wedding-guests/counts'),
      ]);
      if (guestsRes.ok) setGuests(await guestsRes.json());
      if (countsRes.ok) setCounts(await countsRes.json());
    } catch (err) {
      console.error('Failed to fetch wedding guests:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [refreshKey, fetchData]);

  function openAdd() { setModalGuest(null); setShowModal(true); }
  function openEdit(guest) { setModalGuest(guest); setShowModal(true); }
  function closeModal() { setShowModal(false); setModalGuest(undefined); }

  async function handleSave(formData) {
    if (modalGuest) {
      const res = await apiFetch(`/api/wedding-guests/${modalGuest.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        const updated = await res.json();
        setGuests((prev) => prev.map((g) => (g.id === updated.id ? updated : g)).sort((a, b) => a.name.localeCompare(b.name)));
      }
    } else {
      const res = await apiFetch('/api/wedding-guests', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        const created = await res.json();
        setGuests((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      }
    }
    await fetchData();
    closeModal();
  }

  async function handleDelete(id) {
    setGuests((prev) => prev.filter((g) => g.id !== id));
    try {
      await apiFetch(`/api/wedding-guests/${id}`, { method: 'DELETE' });
      await fetchData();
    } catch { await fetchData(); }
  }

  const [exporting, setExporting] = useState(false);
  async function handleExport() {
    setExporting(true);
    try {
      const res = await apiFetch('/api/wedding-guests/export');
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `wedding-guests-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export wedding guests:', err);
    } finally {
      setExporting(false);
    }
  }

  const filteredGuests = guests.filter((g) => {
    if (hideDeclined && g.rsvpResponse === 'declined') return false;
    if (hideAttending && g.rsvpResponse === 'attending') return false;
    if (activeFilter !== 'all' && g.status !== activeFilter) return false;
    if (sideFilter !== 'all' && g.side !== sideFilter) return false;
    if (inviteFilter === 'sent' && !g.inviteSent) return false;
    if (inviteFilter === 'not_sent' && g.inviteSent) return false;
    return true;
  });

  const statusTabs = [
    { key: 'all', label: 'All' },
    { key: 'desired', label: 'Desired' },
    { key: 'possible', label: 'Possible' },
    { key: 'save_the_date', label: 'Save the Date' },
    { key: 'rsvped', label: "RSVP'd" },
  ];

  return (
    <div className="card">
      <h2>Wedding Guest Tracker</h2>

      {counts && (
        <div className="wg-summary-bar">
          <div className="wg-summary-item wg-summary-total">
            <span className="wg-summary-num">{counts.totalInvitations}</span>
            <span className="wg-summary-lbl">invitations</span>
            {counts.totalAdditionalGuests > 0 && (
              <>
                <span className="wg-summary-sep">+</span>
                <span className="wg-summary-num">{counts.totalAdditionalGuests}</span>
                <span className="wg-summary-lbl">guests</span>
              </>
            )}
            <span className="wg-summary-sep">=</span>
            <span className="wg-summary-num">{counts.totalHeadcount}</span>
            <span className="wg-summary-lbl">headcount</span>
          </div>
          <div className="wg-summary-sides">
            <span className="wg-summary-side" style={{ color: SIDE_COLORS.Tim }}>
              Tim: {counts.timHeadcount}
            </span>
            <span className="wg-summary-side" style={{ color: SIDE_COLORS.Emilio }}>
              Emilio: {counts.emilioHeadcount}
            </span>
          </div>
          <div className="wg-summary-statuses">
            {Object.entries(STATUS_LABELS).map(([key, label]) => {
              const cnt = (counts.byStatus && counts.byStatus[key]) || 0;
              const hc = (counts.byStatusHeadcount && counts.byStatusHeadcount[key]) || 0;
              if (cnt === 0) return null;
              return (
                <span key={key} className="wg-summary-status-badge"
                  style={{ background: STATUS_COLORS[key] + '33', color: STATUS_COLORS[key] }}>
                  {label}: {cnt} ({hc})
                </span>
              );
            })}
          </div>
          <div className="wg-summary-statuses">
            <span className="wg-summary-status-badge" style={{ background: '#34d39933', color: '#34d399' }}>
              Invites Sent: {counts.invitesSent}
            </span>
            <span className="wg-summary-status-badge" style={{ background: '#34d39933', color: '#34d399' }}>
              Attending: {counts.rsvpAttending} ({counts.rsvpAttendingHeadcount})
            </span>
            {counts.rsvpDeclined > 0 && (
              <span className="wg-summary-status-badge" style={{ background: '#f8717133', color: '#f87171' }}>
                Declined: {counts.rsvpDeclined}
              </span>
            )}
          </div>
        </div>
      )}

      <div className="wg-controls">
        <div className="wg-filter-tabs">
          {statusTabs.map((tab) => (
            <button key={tab.key}
              className={`wg-filter-tab${activeFilter === tab.key ? ' active' : ''}`}
              onClick={() => setActiveFilter(tab.key)}>{tab.label}</button>
          ))}
        </div>
        <div className="wg-side-filter">
          {['all', 'Tim', 'Emilio'].map((s) => (
            <button key={s}
              className={`wg-side-filter-btn${sideFilter === s ? ' active' : ''}`}
              style={sideFilter === s && s !== 'all'
                ? { background: SIDE_COLORS[s] + '33', color: SIDE_COLORS[s], borderColor: SIDE_COLORS[s] } : {}}
              onClick={() => setSideFilter(s)}>{s === 'all' ? 'All' : s}</button>
          ))}
        </div>
        <div className="wg-side-filter">
          {[
            { key: 'all', label: 'All Invites' },
            { key: 'sent', label: 'Invite Sent' },
            { key: 'not_sent', label: 'Not Invited' },
          ].map((opt) => (
            <button key={opt.key}
              className={`wg-side-filter-btn${inviteFilter === opt.key ? ' active' : ''}`}
              style={inviteFilter === opt.key && opt.key !== 'all'
                ? opt.key === 'sent'
                  ? { background: '#34d39933', color: '#34d399', borderColor: '#34d399' }
                  : { background: '#fbbf2433', color: '#fbbf24', borderColor: '#fbbf24' }
                : {}}
              onClick={() => setInviteFilter(opt.key)}>{opt.label}</button>
          ))}
        </div>
        <label className="settings-toggle-label" style={{ fontSize: 13, gap: 6 }}>
          <input type="checkbox" checked={hideDeclined} onChange={(e) => setHideDeclined(e.target.checked)} />
          <span>Hide declined</span>
        </label>
        <label className="settings-toggle-label" style={{ fontSize: 13, gap: 6 }}>
          <input type="checkbox" checked={hideAttending} onChange={(e) => setHideAttending(e.target.checked)} />
          <span>Hide attending</span>
        </label>
        <button className="wg-add-btn" onClick={openAdd}>+ Add Guest</button>
        <button className="wg-add-btn" onClick={handleExport} disabled={exporting} title="Download Excel file">
          {exporting ? 'Exporting...' : 'Export to Excel'}
        </button>
      </div>

      <div className="wg-guest-list">
        {loading && <div className="wg-loading">Loading...</div>}
        {!loading && filteredGuests.length === 0 && <div className="wg-empty">No guests found</div>}
        {filteredGuests.map((guest) => (
          <div key={guest.id} className="wg-guest-row">
            <div className="wg-guest-info">
              <span className="wg-guest-name">
                {guest.name}
                {guest.headcount > 1 && (
                  <span className="wg-headcount-badge">+{guest.headcount - 1}</span>
                )}
              </span>
              {guest.additionalGuests && guest.additionalGuests.length > 0 && (
                <span className="wg-guest-plusones">
                  {guest.additionalGuests.filter((g) => g).map((g, i) => (
                    <span key={i} className="wg-plusone-name">{g}</span>
                  ))}
                  {guest.additionalGuests.filter((g) => !g).length > 0 && (
                    <span className="wg-plusone-unnamed">
                      {guest.additionalGuests.filter((g) => !g).length} unnamed
                    </span>
                  )}
                </span>
              )}
              <span className="wg-guest-icons">
                {guest.inviteSent && <span title="Invite sent">&#x2709;</span>}
                {guest.rsvpResponse === 'attending' && <span className="wg-rsvp-yes" title="Attending">&#x2713;</span>}
                {guest.rsvpResponse === 'declined' && <span className="wg-rsvp-no" title="Declined">&#x2717;</span>}
                {guest.dietaryRestrictions && <span title={guest.dietaryRestrictions}>&#x1F374;</span>}
                {guest.notes && <span title={guest.notes}>&#9432;</span>}
              </span>
            </div>
            <div className="wg-guest-badges">
              <span className="wg-badge wg-side-badge"
                style={{ background: SIDE_COLORS[guest.side] + '33', color: SIDE_COLORS[guest.side] }}>
                {guest.side}
              </span>
              <span className="wg-badge wg-status-badge"
                style={{ background: STATUS_COLORS[guest.status] + '33', color: STATUS_COLORS[guest.status] }}>
                {STATUS_LABELS[guest.status] || guest.status}
              </span>
              {guest.relationship && (
                <span className="wg-badge wg-relationship-badge"
                  style={{ background: (RELATIONSHIP_COLORS[guest.relationship] || '#71717a') + '33', color: RELATIONSHIP_COLORS[guest.relationship] || '#71717a' }}>
                  {RELATIONSHIP_LABELS[guest.relationship] || guest.relationship}
                </span>
              )}
            </div>
            <div className="wg-guest-actions">
              <button className="wg-action-btn wg-edit-btn" onClick={() => openEdit(guest)} title="Edit">&#x270E;</button>
              <button className="wg-action-btn wg-delete-btn" onClick={() => handleDelete(guest.id)} title="Delete">&times;</button>
            </div>
          </div>
        ))}
      </div>

      {showModal && <GuestModal guest={modalGuest} onSave={handleSave} onClose={closeModal} />}
    </div>
  );
}

export default WeddingGuestTracker;
